import { defineStore } from 'pinia';
import { defineAsyncComponent } from 'vue';
import { usePermissionsStore } from './permissionsStore';
import { router } from '../router';
import {
  resolveModules,
  resolveSidebar,
  resolveAccountMenu,
  type ModuleConfig,
  type ResolvedSidebarItem,
  type ResolvedAccountMenu,
} from '../config/moduleRegistry';

// Import SVG assets
import schedulerIconUrl from '../assets/scheduler.svg';
import managerIconUrl from '../assets/manager.svg';
import settingsIconUrl from '../assets/settings.svg';
import optimizationIconUrl from '../assets/optimization.svg';
import accountIconUrl from '../assets/account.svg';
import workersIconUrl from '../assets/scheduler.svg';
import capacityIconUrl from '../assets/capacity.svg';
import brainIconUrl from '../assets/brain.svg';
import agentsIconUrl from '../assets/agents.svg';
import opsIconUrl from '../assets/ops.svg';
import dashboardIconUrl from '../assets/dashboard.svg';
import agentIconUrl from '../assets/agent.svg';
import materialDemandIconUrl from '../assets/material-demand.svg';
import algorithmRulesIconUrl from '../assets/algorithm-rules.svg';
import revenueScorecardIconUrl from '../assets/revenue-scorecard.svg';
import emsQuoteIconUrl from '../assets/ems-quote.svg';
import reportIconUrl from '../assets/report.svg';

// Import loading and error components for async components
import AppLoading from '../components/AppLoading.vue';
import AppError from '../components/AppError.vue';

// ---------------------------------------------------------------------------
// AppDefinition -- the resolved form with an actual Vue component attached.
// This is what the rest of the app consumes.
// ---------------------------------------------------------------------------

export interface AppDefinition {
  id: string;
  moduleId: string; // Maps to permission module ID
  name: string;
  iconUrl: string;
  component: ReturnType<typeof defineAsyncComponent>; // Type for async component definition
  showGlobalHeader?: boolean; // Whether to show the global header for this app (defaults to true)
}

// ---------------------------------------------------------------------------
// Core module definitions -- registered into the module registry.
// The ORDER here is the DEFAULT sidebar order.
// ---------------------------------------------------------------------------

export const coreModuleDefinitions: ModuleConfig[] = [
  {
    id: 'productionScheduler',
    moduleId: 'production-scheduler',
    slug: 'production-scheduler',
    name: 'Production Scheduler',
    iconUrl: schedulerIconUrl,
    showGlobalHeader: false,
    tabs: [
      { slug: 'scheduler', name: 'Scheduler' },
      { slug: 'jobs', name: 'Jobs' },
      { slug: 'changes', name: 'Changes' },
      { slug: 'capacity', name: 'Capacity' },
      { slug: 'analysis', name: 'Analysis' },
    ],
    componentLoader: () => defineAsyncComponent({
      loader: () => import('../modules/production-scheduler/components/ProductionSchedulerApp.vue'),
      loadingComponent: AppLoading,
      errorComponent: AppError,
      delay: 200,
      timeout: 10000,
    }),
  },
  {
    id: 'productionManager',
    moduleId: 'production-manager',
    slug: 'production-manager',
    name: '3-Day Production',
    iconUrl: managerIconUrl,
    componentLoader: () => defineAsyncComponent({
      loader: () => import('../modules/production-manager/components/ProductionManagerApp.vue'),
      loadingComponent: AppLoading,
      errorComponent: AppError,
      delay: 200,
      timeout: 10000,
    }),
  },
  {
    id: 'workerSkills',
    moduleId: 'worker-skills',
    slug: 'worker-skills',
    name: 'Worker Skills & Scheduling',
    iconUrl: workersIconUrl,
    tabs: [
      { slug: 'skills', name: 'Skills' },
      { slug: 'optimization', name: 'Optimization' },
    ],
    componentLoader: () => defineAsyncComponent({
      loader: () => import('../modules/worker-skills/components/WorkerSkillsApp.vue'),
      loadingComponent: AppLoading,
      errorComponent: AppError,
      delay: 200,
      timeout: 10000,
    }),
  },
  {
    id: 'capacityView',
    moduleId: 'capacity-view',
    slug: 'capacity-view',
    name: 'Product Capacity',
    iconUrl: capacityIconUrl,
    componentLoader: () => defineAsyncComponent({
      loader: () => import('../modules/capacity-view/components/CapacityViewApp.vue'),
      loadingComponent: AppLoading,
      errorComponent: AppError,
      delay: 200,
      timeout: 10000,
    }),
  },
  {
    id: 'userSettings',
    moduleId: 'user-settings',
    slug: 'user-settings',
    name: 'Admin Panel',
    iconUrl: accountIconUrl,
    tabs: [
      { slug: 'users', name: 'Users' },
    ],
    componentLoader: () => defineAsyncComponent({
      loader: () => import('../modules/user-settings/components/UserSettingsApp.vue'),
      loadingComponent: AppLoading,
      errorComponent: AppError,
      delay: 200,
      timeout: 10000,
    }),
  },
  {
    id: 'optimizationTest',
    moduleId: 'optimization-test',
    slug: 'optimization-test',
    name: 'Optimization Test',
    iconUrl: optimizationIconUrl,
    componentLoader: () => defineAsyncComponent({
      loader: () => import('../modules/optimization-test/components/OptimizationTestApp.vue'),
      loadingComponent: AppLoading,
      errorComponent: AppError,
      delay: 200,
      timeout: 10000,
    }),
  },
  {
    id: 'dailyProductionStatus',
    moduleId: 'daily-production-status',
    slug: 'daily-production-status',
    name: 'Daily Production Status',
    iconUrl: dashboardIconUrl,
    componentLoader: () => defineAsyncComponent({
      loader: () => import('../modules/daily-production-status/components/DailyProductionStatusApp.vue'),
      loadingComponent: AppLoading,
      errorComponent: AppError,
      delay: 200,
      timeout: 10000,
    }),
  },
  {
    id: 'revenueScorecard',
    moduleId: 'revenue-scorecard',
    slug: 'revenue-scorecard',
    name: 'Revenue Scorecard',
    iconUrl: revenueScorecardIconUrl,
    showGlobalHeader: false,
    componentLoader: () => defineAsyncComponent({
      loader: () => import('../modules/revenue-scorecard/components/RevenueScorecardApp.vue'),
      loadingComponent: AppLoading,
      errorComponent: AppError,
      delay: 200,
      timeout: 10000,
    }),
  },
  {
    id: 'bridgeConnector',
    moduleId: 'bridge-connector',
    slug: 'connector',
    name: 'Bridge Connector',
    iconUrl: agentIconUrl,
    showGlobalHeader: false,
    tabs: [],
    componentLoader: () => defineAsyncComponent({
      loader: () => import('../modules/connector-config/components/ConnectorConfigApp.vue'),
      loadingComponent: AppLoading,
      errorComponent: AppError,
      delay: 200,
      timeout: 10000,
    }),
  },
  {
    id: 'materialDemand',
    moduleId: 'material-demand',
    slug: 'material-demand',
    name: 'Material Demand',
    iconUrl: materialDemandIconUrl,
    showGlobalHeader: false,
    componentLoader: () => defineAsyncComponent({
      loader: () => import('../modules/material-demand/components/MaterialDemandApp.vue'),
      loadingComponent: AppLoading,
      errorComponent: AppError,
      delay: 200,
      timeout: 10000,
    }),
  },
  {
    id: 'algorithmRules',
    moduleId: 'algorithm-rules',
    slug: 'algorithm-rules',
    name: 'Algorithm Rules',
    iconUrl: algorithmRulesIconUrl,
    showGlobalHeader: false,
    tabs: [
      { slug: 'scoring', name: 'Scoring' },
      { slug: 'placement', name: 'Placement' },
      { slug: 'employee', name: 'Employee' },
    ],
    componentLoader: () => defineAsyncComponent({
      loader: () => import('../modules/algorithm-rules/components/AlgorithmRulesApp.vue'),
      loadingComponent: AppLoading,
      errorComponent: AppError,
      delay: 200,
      timeout: 10000,
    }),
  },
  {
    id: 'emsQuote',
    moduleId: 'ems-quote',
    slug: 'ems-quote',
    name: 'EMS Quote',
    iconUrl: emsQuoteIconUrl,
    componentLoader: () => defineAsyncComponent({
      loader: () => import('../modules/ems-quote/components/EMSQuoteApp.vue'),
      loadingComponent: AppLoading,
      errorComponent: AppError,
      delay: 200,
      timeout: 10000,
    }),
  },
  {
    id: 'brain',
    moduleId: 'brain',
    slug: 'brain',
    name: 'Brain',
    iconUrl: brainIconUrl,
    showGlobalHeader: false,
    componentLoader: () => defineAsyncComponent({
      loader: () => import('../modules/brain/components/BrainApp.vue'),
      loadingComponent: AppLoading,
      errorComponent: AppError,
      delay: 200,
      timeout: 10000,
    }),
  },
  {
    id: 'agentConsole',
    moduleId: 'agent-console',
    slug: 'agents',
    name: 'Agents',
    iconUrl: agentsIconUrl,
    showGlobalHeader: false,
    componentLoader: () => defineAsyncComponent({
      loader: () => import('../modules/agent-console/components/AgentConsoleApp.vue'),
      loadingComponent: AppLoading,
      errorComponent: AppError,
      delay: 200,
      timeout: 10000,
    }),
  },
  {
    id: 'ops',
    moduleId: 'ops',
    slug: 'ops',
    name: 'Monitoring', // jobs, connections, usage — "Operations" is the tenant's plant-floor category
    iconUrl: opsIconUrl,
    showGlobalHeader: false,
    componentLoader: () => defineAsyncComponent({
      loader: () => import('../modules/ops/components/OpsApp.vue'),
      loadingComponent: AppLoading,
      errorComponent: AppError,
      delay: 200,
      timeout: 10000,
    }),
  },
  {
    id: 'settings',
    moduleId: 'settings',
    slug: 'settings',
    name: 'Settings',
    iconUrl: settingsIconUrl,
    componentLoader: () => defineAsyncComponent({
      loader: () => import('../modules/settings/components/SettingsApp.vue'),
      loadingComponent: AppLoading,
      errorComponent: AppError,
      delay: 200,
      timeout: 10000,
    }),
  },
  {
    id: 'changeHistory',
    moduleId: 'change-history',
    slug: 'change-history',
    name: 'Change History',
    iconUrl: reportIconUrl,
    componentLoader: () => defineAsyncComponent({
      loader: () => import('../modules/change-history/components/ChangeHistoryApp.vue'),
      loadingComponent: AppLoading,
      errorComponent: AppError,
      delay: 200,
      timeout: 10000,
    }),
  },
];

// ---------------------------------------------------------------------------
// Helper: convert a ModuleConfig into an AppDefinition
// ---------------------------------------------------------------------------

export function toAppDefinition(config: ModuleConfig): AppDefinition {
  return {
    id: config.id,
    moduleId: config.moduleId,
    name: config.name,
    iconUrl: config.iconUrl,
    showGlobalHeader: config.showGlobalHeader,
    component: config.componentLoader(),
  };
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

interface AppState {
  globalHeaderEnabled: boolean;
}

export const useAppStore = defineStore('app', {
  state: (): AppState => ({
    globalHeaderEnabled: true,
  }),
  getters: {
    /** All registered modules resolved through the registry (respects tenant overrides). */
    allApps(): AppDefinition[] {
      return resolveModules().map(toAppDefinition);
    },

    /** Modules the current user has permission to access. */
    availableApps(): AppDefinition[] {
      const permissionsStore = usePermissionsStore();
      return this.allApps.filter(app => permissionsStore.hasPermission(app.moduleId));
    },

    /**
     * The ID of the currently active module, derived from the route.
     * Returns null when:
     *   - the router hasn't been instantiated yet (boot window),
     *   - the matched route is the placeholder '__root' (during the initial redirect to a module),
     *   - the matched route name is a symbol (not used today, but a safety net).
     */
    currentAppId(): string | null {
      // router.currentRoute is a ComputedRef — reading .value here keeps the
      // getter reactive when consumed from a Vue template/computed/watcher.
      // Returns null during the boot window (router undefined), for symbol-named
      // routes, and for the placeholder '__root' route during the initial redirect.
      const name = router?.currentRoute.value.name;
      if (typeof name !== 'string' || name === '__root') return null;
      return name;
    },

    /** The currently selected module. */
    currentApp(): AppDefinition | undefined {
      return this.availableApps.find(app => app.id === this.currentAppId);
    },

    /** Whether the global header should be shown for the current module. */
    shouldShowGlobalHeader(): boolean {
      if (!this.globalHeaderEnabled) return false;
      const app = this.currentApp;
      return app ? (app.showGlobalHeader !== false) : true;
    },

    /** Resolved sidebar items (modules and categories with full ModuleConfig). */
    resolvedSidebar(): ResolvedSidebarItem[] {
      return resolveSidebar();
    },

    /** Resolved account menu (children modules + labeled actions). */
    resolvedAccount(): ResolvedAccountMenu {
      return resolveAccountMenu();
    },
  },
  actions: {
    setGlobalHeaderEnabled(enabled: boolean) {
      this.globalHeaderEnabled = enabled;
    },
  },
});
